import React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useSet } from 'react-use';
import { Accordion } from '../../../../../../../CommonComponents/OPL/Accordion/Accordion/Accordion';
import { AccordionButton } from '../../../../../../../CommonComponents/OPL/Accordion/AccordionButton/AccordionButton';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../../CommonComponents/OPL/Button/Button';
import { Checkbox } from '../../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { IconButton } from '../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Input } from '../../../../../../../CommonComponents/OPL/Input/Input';
import { InputNumberMask } from '../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Radio } from '../../../../../../../CommonComponents/OPL/Radio/Radio';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Separator } from '../../../../../../../CommonComponents/OPL/Separator/Separator';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { selectCurrentClient } from '../../../../../../../CommonSelectors/CommonAreaSelector';
import { WindfallOptions } from '../../../../api/useWhatIfOptionsQuery';
import { StyledAccordionPanel } from '../../WhatIf.styles';
import { FormValues, TaxStatusValues, Windfall as WindfallDTO } from './Windfall.meta';

interface Props {
  Title: string;
  options: WindfallOptions;
}

export const Windfall = ({ Title, options }: Props) => {
  const [, { has: isWindfallCollapsed, toggle: toggleWindfall }] = useSet<string | null>();

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>();

  const windfallsFieldArray = useFieldArray({
    control,
    name: 'WindfallEvents',
  });

  const watchWindfalls = watch('WindfallEvents');

  const windfalls = windfallsFieldArray.fields.map((field, index) => ({
    ...field,
    ...watchWindfalls[index],
  }));

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const yearOptions = options.YearOptions.map(year => ({
    label: year.toString(),
    value: year,
    rightLabel: primaryYob
      ? `Age ${year - primaryYob}${spouseYob ? `, Age ${year - spouseYob}` : ''}`
      : undefined,
  }));

  const earmarkOptions = options.EarmarkOptions.map(opt => ({
    label: opt.Label,
    value: opt.Value,
  }));

  const allOwnerOptions = [...options.OwnersOptions.People, ...options.OwnersOptions.Trusts];

  const NEW_WINDFALL: Partial<WindfallDTO> = {
    WindfallYear: null,
    WindfallNetAmount: null,
    WindfallDescription: '',
    WindfallEarmark: [],
    WindfallTaxStatus: TaxStatusValues[0],
    WindfallOwnerName: allOwnerOptions[0],
    TreatAsInheritedIRA: false,
    TreatAsInheritedRothIRA: false,
    IsApplied: false,
  };

  return (
    <>
      <Text fontSize="h5" fontWeight={600} px="12" py="16">
        What-if...
      </Text>

      <Separator />

      <Box display="flex" flexDirection="column" p={12} style={{ gap: 16 }}>
        <Text fontSize="h6" fontWeight={500}>
          {Title}
        </Text>
      </Box>

      <Accordion isActive={false} css="border: none; border-radius: 0; ">
        {windfalls.map((windfall, index) => {
          const buttonId = `what-if-windfall--accordion-button--${windfall.id}`;
          const panelId = `what-if-windfall--accordion-panel--${windfall.id}`;
          const selectedTaxStatus = watch(`WindfallEvents.${index}.WindfallTaxStatus`);
          const showInheritedIRACheckbox =
            selectedTaxStatus === TaxStatusValues[1] ||
            selectedTaxStatus === TaxStatusValues.indexOf('TaxDeferred');
          const showInheritedRothIRACheckbox =
            selectedTaxStatus === TaxStatusValues[2] ||
            selectedTaxStatus === TaxStatusValues.indexOf('TaxFree');

          const isTaxable =
            selectedTaxStatus === TaxStatusValues[0] ||
            selectedTaxStatus === TaxStatusValues.indexOf('Taxable');

          const filteredOwnerOptions = allOwnerOptions
            .filter(owner => {
              if (isTaxable) return true;
              const isTrust = options.OwnersOptions.Trusts.includes(owner);
              return owner !== 'Joint' && !isTrust;
            })
            .map(owner => ({
              label: owner,
              value: owner,
            }));

          return (
            <React.Fragment key={windfall.id}>
              <AccordionButton
                id={buttonId}
                panelId={panelId}
                isActive={!isWindfallCollapsed(windfall.id)}
                onClick={() => toggleWindfall(windfall.id)}
                extra={
                  !windfall.IsApplied && (
                    <IconButton
                      variant="subtleInactive"
                      icon="far fa-minus-circle"
                      onClick={() => windfallsFieldArray.remove(index)}
                    />
                  )
                }
              >
                {`Windfall event ${index + 1}`}
              </AccordionButton>

              <StyledAccordionPanel
                id={panelId}
                buttonId={buttonId}
                isActive={!isWindfallCollapsed(windfall.id)}
              >
                <Box display="flex" flexDirection="column" gridGap="16" px="16" pb="32">
                  <Controller
                    control={control}
                    name={`WindfallEvents.${index}.WindfallNetAmount`}
                    render={({ field: { onChange, ...field } }) => (
                      <InputNumberMask
                        label="Net Amount of Windfall"
                        decimalScale={2}
                        allowNegative={false}
                        leftIcon="far fa-dollar-sign"
                        formVariant={
                          errors.WindfallEvents?.[index]?.WindfallNetAmount ? 'error' : undefined
                        }
                        helperText={errors.WindfallEvents?.[index]?.WindfallNetAmount?.message}
                        onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    name={`WindfallEvents.${index}.WindfallYear`}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        label="Year of Windfall"
                        placeholder="Select Year"
                        options={yearOptions}
                        value={yearOptions.find(option => option.value === value)}
                        onChange={option => onChange(option?.value ?? null)}
                        formVariant={
                          errors.WindfallEvents?.[index]?.WindfallYear ? 'error' : undefined
                        }
                        helperText={errors.WindfallEvents?.[index]?.WindfallYear?.message}
                        isPortal
                      />
                    )}
                  />
                  <Controller
                    name={`WindfallEvents.${index}.WindfallEarmark`}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        isMulti
                        label="Earmark"
                        value={earmarkOptions.filter(option =>
                          value?.some(val => JSON.stringify(val) === JSON.stringify(option.value)),
                        )}
                        placeholder="Select Earmark"
                        options={earmarkOptions}
                        onChange={earmarks => {
                          onChange(earmarks.map(option => option.value));
                        }}
                        isPortal
                      />
                    )}
                  />
                  <Input
                    label="Description"
                    {...register(`WindfallEvents.${index}.WindfallDescription`)}
                  />
                  <Text fontSize="small" fontWeight={600} color="neutral.600" mt="4">
                    Tax Status
                  </Text>
                  <Controller
                    name={`WindfallEvents.${index}.WindfallTaxStatus`}
                    control={control}
                    render={({ field: { value, name, onChange } }) => {
                      const selectedLabel =
                        typeof value === 'number' ? TaxStatusValues[value] : value;
                      return (
                        <Box display="flex" flexDirection="row" css="gap: 8px">
                          {options.TaxStatusOptions.map((option, id) => (
                            <Radio
                              key={option}
                              name={name}
                              checked={selectedLabel === TaxStatusValues[id]}
                              onChange={() => {
                                onChange(TaxStatusValues[id]);

                                if (TaxStatusValues[id] === 'TaxDeferred') {
                                  setValue(`WindfallEvents.${index}.TreatAsInheritedIRA`, true);
                                  setValue(
                                    `WindfallEvents.${index}.TreatAsInheritedRothIRA`,
                                    false,
                                  );
                                } else if (TaxStatusValues[id] === 'TaxFree') {
                                  setValue(`WindfallEvents.${index}.TreatAsInheritedIRA`, false);
                                  setValue(`WindfallEvents.${index}.TreatAsInheritedRothIRA`, true);
                                } else {
                                  setValue(`WindfallEvents.${index}.TreatAsInheritedIRA`, false);
                                  setValue(
                                    `WindfallEvents.${index}.TreatAsInheritedRothIRA`,
                                    false,
                                  );
                                }
                              }}
                            >
                              <Text fontSize="small" fontWeight={600} color="neutral.600" mt="4">
                                {option}
                              </Text>
                            </Radio>
                          ))}
                        </Box>
                      );
                    }}
                  />

                  {showInheritedIRACheckbox && (
                    <Controller
                      name={`WindfallEvents.${index}.TreatAsInheritedIRA`}
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Checkbox
                          checked={value}
                          onChange={e => {
                            onChange(e.target.checked);
                            if (e.target.checked) {
                              setValue(`WindfallEvents.${index}.TreatAsInheritedRothIRA`, false);
                            }
                          }}
                        >
                          <Text fontWeight={600} color="neutral.600" fontSize="small">
                            This will be an Inherited IRA
                          </Text>
                        </Checkbox>
                      )}
                    />
                  )}

                  {showInheritedRothIRACheckbox && (
                    <Controller
                      name={`WindfallEvents.${index}.TreatAsInheritedRothIRA`}
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Checkbox
                          checked={value}
                          onChange={e => {
                            onChange(e.target.checked);
                            if (e.target.checked) {
                              setValue(`WindfallEvents.${index}.TreatAsInheritedIRA`, false);
                            }
                          }}
                        >
                          <Text fontWeight={600} color="neutral.600" fontSize="small">
                            This will be an Inherited Roth IRA
                          </Text>
                        </Checkbox>
                      )}
                    />
                  )}

                  <Controller
                    name={`WindfallEvents.${index}.WindfallOwnerName`}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        label="Owner"
                        placeholder="Select Owner"
                        options={filteredOwnerOptions}
                        value={filteredOwnerOptions.find(option => option.value === value)}
                        onChange={option => onChange(option?.value ?? null)}
                        isPortal
                      />
                    )}
                  />
                </Box>
              </StyledAccordionPanel>
            </React.Fragment>
          );
        })}
      </Accordion>

      <Button
        variant="link"
        leftIcon="fas fa-plus"
        my="24"
        ml="16"
        onClick={() => windfallsFieldArray.append(NEW_WINDFALL as WindfallDTO)}
      >
        {`Add ${windfalls.length > 0 ? 'another ' : ''}windfall event`}
      </Button>
    </>
  );
};
